import { IntegrationStatusTypes } from 'module/api/integrations/salesforce/types';
import { Tabs as TabsAtom } from 'module/ui/components/molecules/tab-container';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import { IconWrapper } from 'components/common/styles';

const isSalesforceV2Host =
  process.env.IS_MODULE_FEDERATION === 'true' ||
  (typeof window !== 'undefined' &&
    window.location.pathname.includes('/settings/integrations/salesforce-v2'));

const SALESFORCE_SETTINGS_INTEGRATION_TEMPLATE = isSalesforceV2Host
  ? 'settings/integrations/salesforce-v2/:integrationId'
  : ROUTE_PATHS.SALESFORCE_SETTINGS_INTEGRATION;

const { TabPane } = TabsAtom;
const Tabs = styled(TabsAtom)`
  && {
    height: auto;
    .ant-tabs-content-holder {
      display: none;
    }
    .ant-tabs-nav {
      padding: 0;
      &::before {
        border-bottom: none;
      }
    }
    .ant-tabs-ink-bar-animated {
      transition: none !important;
    }
  }
`;

export enum SalesforceTabsType {
  Automations = 'automations',
  FieldSync = 'field-sync',
  ErrorLogs = 'error-logs',
  Settings = 'settings',
}

interface SalesforceTabsProps {
  activeKey: SalesforceTabsType;
  integrationId?: string;
  integrationStatus?: IntegrationStatusTypes;
}

// Protected tabs that require authentication
const protectedTabs = [
  SalesforceTabsType.Automations,
  SalesforceTabsType.FieldSync,
  SalesforceTabsType.ErrorLogs,
];

const getRelativeUrl = (key: SalesforceTabsType) => {
  switch (key) {
    case SalesforceTabsType.Automations:
      return ROUTE_PATHS.SALESFORCE_AUTOMATIONS;
    case SalesforceTabsType.FieldSync:
      return ROUTE_PATHS.SALESFORCE_FIELD_SYNC;
    case SalesforceTabsType.ErrorLogs:
      return ROUTE_PATHS.SALESFORCE_ERROR_LOGS;
    case SalesforceTabsType.Settings:
      return ROUTE_PATHS.SALESFORCE_SETTINGS_TAB;
  }
};

const salesforceTabs = [
  {
    key: SalesforceTabsType.Automations,
    label: <FormattedMessage id="salesforce.tabs.automations" />,
  },
  {
    key: SalesforceTabsType.FieldSync,
    label: <FormattedMessage id="salesforce.tabs.field-sync" />,
  },
  {
    key: SalesforceTabsType.Settings,
    label: <FormattedMessage id="salesforce.tabs.settings" />,
  },
  {
    key: SalesforceTabsType.ErrorLogs,
    label: <FormattedMessage id="salesforce.tabs.error-logs" />,
  },
];

const getDisabledStatus = (
  tabKey: SalesforceTabsType,
  integrationId?: string,
  integrationStatus?: IntegrationStatusTypes,
): boolean => {
  if (tabKey === SalesforceTabsType.Settings) {
    return false;
  }

  if (!protectedTabs.includes(tabKey)) {
    return false;
  }

  const isNotAuthenticated =
    integrationId === 'new' ||
    integrationStatus === IntegrationStatusTypes.Expired;

  return isNotAuthenticated;
};

export const SalesforceTabs = ({
  activeKey,
  integrationId,
  integrationStatus,
}: SalesforceTabsProps) => {
  const params = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const currentIntegrationId = integrationId ?? params.integrationId;

  const onChange = useCallback(
    (key: string) => {
      if (currentIntegrationId == null) {
        return;
      }
      const relativeUrl = getRelativeUrl(key as SalesforceTabsType);
      const baseUrl = SALESFORCE_SETTINGS_INTEGRATION_TEMPLATE.replace(
        ':integrationId',
        currentIntegrationId,
      );

      navigate(`/${baseUrl}/${relativeUrl}`);
    },
    [navigate, currentIntegrationId],
  );

  return (
    <Tabs activeKey={activeKey} onChange={onChange}>
      {salesforceTabs.map((tab) => {
        const isDisabled = getDisabledStatus(
          tab.key,
          currentIntegrationId,
          integrationStatus,
        );

        return (
          <TabPane
            key={tab.key}
            tab={<IconWrapper>{tab.label}</IconWrapper>}
            disabled={isDisabled}
          />
        );
      })}
    </Tabs>
  );
};
